import { Button, Container, Header, Text } from '@gasbuddy/react-components';
import { useDimensions } from '@gasbuddy/react-hooks';
import classnames from 'classnames/bind';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import Breadcrumb from '../Breadcrumb';
import Markup from '../Markup';
import Page from '../Page';
import styles from './TimelinePage.module.css';

const cx = classnames.bind(styles);

const MILESTONES = [2000, 2001, 2006, 2008, 2010, 2013, 2015, 2016, 2017, 2018, 2019, 2020];

function Milestone({ description, onBottomChange, side, year }) {
  const contentRef = useRef();
  const { bottom } = useDimensions(contentRef);

  useEffect(() => {
    if (onBottomChange) {
      onBottomChange(bottom);
    }
  }, [bottom, onBottomChange]);

  return (
    <div className={cx('milestone', { left: side === 'left', right: side === 'right' })}>
      <div ref={contentRef}>
        <div>
          <Text bold className={cx('year')} size="xl">{year}</Text>
        </div>
        <Markup source={description} />
      </div>
    </div>
  );
}

Milestone.propTypes = {
  description: PropTypes.string,
  onBottomChange: PropTypes.func,
  side: PropTypes.oneOf(['left', 'right']),
  year: PropTypes.number.isRequired,
};

Milestone.defaultProps = {
  description: undefined,
  onBottomChange: undefined,
  side: 'left',
};

function determineSide(index, bottoms = []) {
  let side;

  if (index >= 2 && bottoms[index]) {
    const previousSide = determineSide(index - 1, bottoms);
    const twoSidesAgo = determineSide(index - 2, bottoms);

    if (previousSide === twoSidesAgo) {
      side = previousSide === 'left' ? 'right' : 'left';
    } else {
      side = bottoms[index - 1] > bottoms[index - 2] ? twoSidesAgo : previousSide;
    }
  } else {
    side = index % 2 ? 'right' : 'left';
  }

  return side;
}

export default function TimelinePage({ className }) {
  const [bottoms, setBottoms] = useState([]);
  const { t } = useTranslation(['timeline', 'miscellaneous']);

  const milestones = useMemo(() => MILESTONES.map(year => ({
    year,
    description: t(`timeline:milestone_${year}`),
  })), [t]);

  const bottomChangeHandlers = useMemo(() => milestones.map((m, index) => (bottom) => {
    setBottoms((existingBottoms) => {
      const newBottoms = [...existingBottoms];
      newBottoms[index] = bottom;
      return newBottoms;
    });
  }), [milestones]);

  return (
    <Page
      description={t('pageDescription', { ns: 'timeline' })}
      name="Timeline"
      title={t('pageTitle', { ns: 'timeline' })}
    >
      <div className={cx('milestonesContainer')}>
        <Container className={cx(className)}>
          <Breadcrumb linkTo="/" showSeparator={false}>
            {t('navigation.home', { ns: 'miscellaneous' })}
          </Breadcrumb>
          <Breadcrumb linkTo="/about">
            {t('navigation.about', { ns: 'miscellaneous' })}
          </Breadcrumb>
          <br />
          <br />
          <br />
          <br />
          <Trans i18nKey="timeline:header">
            <Header as="h1" className={cx('header')} size="sm">
              Our journey to becoming<br />the most ways, most places to save money on gas
            </Header>
          </Trans>
          {milestones.map(({ description, year }, i) => (
            <Milestone
              description={description}
              key={year}
              onBottomChange={bottomChangeHandlers[i]}
              side={determineSide(i, bottoms)}
              year={year}
            />
          ))}
        </Container>
      </div>
      <div className={cx('learnMoreContainer')}>
        <Container>
          <Header as="h2" className={cx('aboutSubHeader')}>
            {t('actions.learnMore', { ns: 'miscellaneous' })}
          </Header>
          <Text as="p" color="grey">
            {t('visitPressPage', { ns: 'timeline' })}
          </Text>
          <br />
          <br />
          <Button
            as="a"
            href="/newsroom"
            primary
            size="lg"
            wide
          >
            {t('navigation.inTheNews', { ns: 'miscellaneous' })}
          </Button>
        </Container>
      </div>
    </Page>
  );
}

TimelinePage.propTypes = {
  className: PropTypes.string,
};

TimelinePage.defaultProps = {
  className: undefined,
};
